import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { products, productGroups } from "@/data/products";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";

export const Products = () => {
  useSeo({
    title: "Products — Sourcing Catalogue India | Asia Sourcing",
    description:
      "Full product catalogue Asia Sourcing sources from India — cookware, handicrafts, marble inlay, brass, textile, carpets, shawls, home décor, garden. 34 categories.",
    path: "/products",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "ItemList",
          name: "Asia Sourcing product catalogue",
          numberOfItems: products.length,
          itemListElement: products.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: p.name,
            url: `${SITE_URL}/products/${p.id}`,
          })),
        },
        breadcrumb([
          { name: "Home", item: `${SITE_URL}/` },
          { name: "Products", item: `${SITE_URL}/products` },
        ]),
      ],
    },
  });

  return (
    <div data-testid="products-page">
      <header className="wrap" style={{ paddingBlock: "calc(74px + var(--section)) 3rem" }}>
        <Reveal>
          <p className="eyebrow" data-testid="products-eyebrow">Catalogue · India sourcing</p>
        </Reveal>
        <WordReveal
          as="h1"
          text="Product catalogue."
          className="display"
          stagger={70}
          testId="products-h1"
        />
        <Reveal as="p" delay={200} style={{ maxWidth: "68ch", color: "var(--ink-soft)", fontSize: "1.12rem", marginTop: "1.4rem", lineHeight: 1.65 }}>
          Every product family Asia Sourcing runs private-label programmes for, organised by discipline. Each landing goes deep on materials, MOQ, lead time, certifications and manufacturing clusters — the buyer's brief before you write the sourcing RFQ.
        </Reveal>
      </header>

      {productGroups.map((group) => (
        <section
          key={group.label}
          className="wrap"
          style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
          data-testid={`products-group-${group.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
        >
          <Reveal as="p" className="eyebrow">{group.label}</Reveal>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "1.4rem",
              marginTop: "2rem",
            }}
          >
            {group.productIds.map((pid) => {
              const p = products.find((x) => x.id === pid);
              if (!p) return null;
              return (
                <Reveal key={p.id}>
                  <Link
                    to={`/products/${p.id}`}
                    style={{
                      display: "block",
                      padding: "1.4rem 1.2rem",
                      border: "1px solid var(--line)",
                      textDecoration: "none",
                      color: "inherit",
                      height: "100%",
                    }}
                    data-testid={`product-tile-${p.id}`}
                  >
                    <p className="mono" style={{ color: "var(--brass-deep)", fontSize: ".64rem", letterSpacing: ".14em", textTransform: "uppercase", margin: 0 }}>
                      {p.parentLabel}
                    </p>
                    <p style={{ fontFamily: "var(--display)", fontSize: "1.2rem", margin: ".55rem 0 .5rem", letterSpacing: "-.005em", lineHeight: 1.25 }}>
                      {p.name}
                    </p>
                    <p style={{ color: "var(--ink-soft)", fontSize: ".92rem", margin: 0, lineHeight: 1.55 }}>
                      {p.metaDescription.substring(0, 130).replace(/\s+\S*$/, "") + "…"}
                    </p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="wrap" style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}>
        <Reveal as="p" className="eyebrow">Not sure where to start?</Reveal>
        <Reveal as="h2" className="display">
          Send a brief. We'll route it.
        </Reveal>
        <Reveal as="div" style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginTop: "2rem" }}>
          <Link to="/contact" className="btn btn-fill btn-lg btn-glow" data-testid="products-cta-contact">
            Start a sourcing brief
          </Link>
          <Link to="/services" className="btn btn-lg" data-testid="products-cta-services">
            See our services
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default Products;
